import React, { useState } from 'react'
import { Box, Alert } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { toast } from 'react-toastify'
import withLayout from '../../hocs/Layout'
import PageTitle from '../../common/PageTitle'
import { apiConstants, getGeneric } from '../../common/constants'
import { downloadCSV } from '../../helpers/UtilityHelper'
import { getMerchant } from '../../helpers/ReduxHelpers'
import { FONT, SIZE } from '../../styles/foundation/typography'
import { WHITE, BORDER, PRIMARY_BLUE } from '../../styles/foundation/colors'
import { SHADOW } from '../../styles/foundation/effects'
import { pageContainerSx, primaryBtnSx } from '../../styles/patterns'

// Download the merchant's full (bulk) serviceability list — every deliverable PIN code
// (COD / Prepaid) plus pickup coverage for the account. Uses tools/serviceability_bulk.
// NOTE: this is intentionally NOT the zone map — that lives on the separate Zone Mapping page.
function DownloadServiceability() {
  const merchant = getMerchant()
  const merchantId = merchant?.id || ''
  const [isLoading, setIsLoading] = useState(false)

  const handleDownload = async () => {
    if (!merchantId) return
    setIsLoading(true)
    try {
      const res = await getGeneric(`${apiConstants.BULK_MERCHANT_SERVICEABILITY}?merchantOid=${merchantId}`)
      const result = res?.data?.result
      const cod = new Set(result?.drop_pincodes?.COD || [])
      const prepaid = new Set(result?.drop_pincodes?.PREPAID || [])
      const pickup = new Set(result?.pickup_pincodes?.PREPAID || [])
      const allPincodes = Array.from(new Set([...cod, ...prepaid, ...pickup])).sort()

      if (allPincodes.length === 0) {
        toast('No serviceability data available for your account', { type: 'info', theme: 'colored' })
        return
      }

      const lines = ['Pincode,Deliverable (COD),Deliverable (Prepaid),Pickup']
      allPincodes.forEach((pin) => {
        lines.push(`${pin},${cod.has(pin) ? 'Yes' : 'No'},${prepaid.has(pin) ? 'Yes' : 'No'},${pickup.has(pin) ? 'Yes' : 'No'}`)
      })
      downloadCSV(lines.join('\n'), `bulk-serviceability-${merchantId}`)
    } catch (e) {
      console.error(e)
      toast('Failed to download serviceability data', { type: 'error', theme: 'colored' })
    } finally {
      setIsLoading(false)
    }
  }

  const cardSx = { bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, p: { xs: 2.5, md: 3.5 } }

  return (
    <Box sx={pageContainerSx}>
      <PageTitle>Download Bulk Serviceability</PageTitle>

      <Box sx={cardSx}>
        <Alert severity='info' icon={<InfoOutlinedIcon />} sx={{ borderRadius: '10px', mb: 2.5, fontFamily: FONT, fontSize: SIZE.xs }}>
          Download a CSV of every PIN code your allocated couriers can serve — showing deliverable
          (COD / Prepaid) and pickup coverage for your account.
        </Alert>

        <LoadingButton
          loading={isLoading}
          onClick={handleDownload}
          variant='contained'
          startIcon={<DownloadOutlinedIcon />}
          sx={{ ...primaryBtnSx, bgcolor: PRIMARY_BLUE, py: 1.25 }}
        >
          Download Bulk Serviceability
        </LoadingButton>
      </Box>
    </Box>
  )
}

export default withLayout(DownloadServiceability)
